import { useState } from "react";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { useAuth } from "@clerk/react";
import { api, errMsg, fmtSlot } from "@/lib/api";

const ReportLink = ({ r }) => {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/reports/${r.id}`, { headers: { Authorization: `Bearer ${await getToken()}` }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errMsg(e, "Could not open the file"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={open} disabled={busy} className="flex items-center gap-1.5 text-xs text-brass-700 font-semibold hover:underline disabled:opacity-50 max-w-full" data-testid={`report-link-${r.id}`}>
      <Paperclip className="w-3 h-3 shrink-0" /><span className="truncate">{r.original_filename}</span>
    </button>
  );
};

export default function BookingsList({ bookings }) {
  if (!bookings) return <p className="text-[#57645A]">Loading…</p>;
  if (bookings.length === 0) return <div className="bg-white border border-parchment-300 rounded-2xl p-10 text-center text-[#57645A]" data-testid="bookings-empty">No consultation bookings yet.</div>;

  return (
    <div className="space-y-3" data-testid="bookings-list">
      {bookings.map((b) => (
        <div key={b.id} className="bg-white border border-parchment-300 rounded-2xl p-5 grid sm:grid-cols-[0.9fr_1.2fr_1.4fr] gap-3" data-testid={`booking-row-${b.id}`}>
          <div>
            <p className="font-serif text-2xl font-bold text-forest-800">{fmtSlot(b.time)}</p>
            <p className="text-xs text-[#57645A]">{new Date(`${b.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
          </div>
          <div>
            <p className="font-semibold text-forest-800">{b.name}</p>
            <a href={`tel:+91${b.phone}`} className="text-sm text-[#57645A]">{b.phone}</a>
            {b.reason && <p className="text-xs text-[#57645A] mt-1">{b.reason}</p>}
          </div>
          <div className="min-w-0 space-y-1">
            {b.reports.length === 0 ? <p className="text-xs text-[#8A978E]">No reports attached</p> : b.reports.map((r) => <ReportLink key={r.id} r={r} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
